import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { NullableType, Params, StateSchema } from '../utils/helpers/types';
import {
  encodeHashParams,
  keyTransformer,
  parseQueryParams,
} from '../utils/searchParams/urlHelpers';
import { useSafeSearchParams } from '../utils/useSafeSearchParams';

type CmsHashStateOptions = {
  /**
   * Keep the hash keys of other schemas when writing. Off by default: a write
   * normally rebuilds the whole hash from this schema, which is how opening one
   * drawer closes another (CMS-1954). Turn it on for UI that opens on top of
   * another hash-driven drawer and must leave it open.
   */
  preserveOtherKeys?: boolean;
};

// pushState fires no event, and Next re-renders only for URL changes it made
// itself — not for an anchor click or a hash the page was loaded with. A write
// tells this hook's readers on its own.
export const HASH_STATE_EVENT = 'mhg:hash-state';

export const useCmsHashState = <T extends Params>(
  stateSchema: StateSchema<T>,
  options?: CmsHashStateOptions,
): [T, (value: NullableType<T>) => void] => {
  const hashString = useHashString();

  let queryStringState: Partial<T> = {};

  const updateState = (newPartialState: NullableType<T>) => {
    const updated = { ...queryStringState, ...newPartialState };
    updateHashString(updated, stateSchema, options?.preserveOtherKeys);
  };

  const hashItems = useMemo(() => {
    if (!hashString) {
      return [];
    }

    return hashString.split('&').map<[string, string]>((item) => {
      const [hashKey, value] = item.split('=');

      return [keyTransformer.parse(hashKey), decodeURIComponent(value)];
    });
  }, [hashString]);

  queryStringState = parseQueryParams(stateSchema, hashItems);

  return [queryStringState as T, updateState];
};

function updateHashString<T extends Params>(
  newHashState: NullableType<T>,
  stateSchema: StateSchema<T>,
  preserveOtherKeys = false,
): void {
  const { pathname, search, hash } = window.location;

  // Other schemas' items, kept verbatim (still encoded) ahead of this schema's.
  const otherItems = preserveOtherKeys
    ? hash
        .substring(1)
        .split('&')
        .filter(
          (item) =>
            item &&
            (stateSchema as Record<string, string>)[
              keyTransformer.parse(item.split('=')[0])
            ] === undefined,
        )
    : [];

  // One encoding with the hash links (`encodeHashParams`), so a link and the
  // state it writes match.
  const inScope = encodeHashParams(
    Object.fromEntries(
      Object.entries(newHashState).filter(([key]) => key in stateSchema),
    ),
  ).substring(1);
  const newHash = [...otherItems, inScope].filter(Boolean).join('&');

  window.history.pushState(
    null,
    '',
    `${pathname}${search}${newHash ? `#${newHash}` : ''}`,
  );
  window.dispatchEvent(new Event(HASH_STATE_EVENT));
}

function useHashString() {
  const [hash, setHash] = useState('');
  const pathname = usePathname();
  const searchParams = useSafeSearchParams();

  useEffect(() => {
    const getHash = () => (window?.location.hash || '').substring(1);

    setHash(getHash());

    const handleHashChange = () => {
      setHash(getHash());
    };

    window.addEventListener('hashchange', handleHashChange);
    window.addEventListener('popstate', handleHashChange);
    window.addEventListener(HASH_STATE_EVENT, handleHashChange);

    return () => {
      window.removeEventListener('popstate', handleHashChange);
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener(HASH_STATE_EVENT, handleHashChange);
    };
  }, [pathname, searchParams]);

  return hash;
}
